/**
 * GroupChat -- Simple group messaging within a community.
 *
 * Features: message list with sender avatar + name + timestamp,
 * text input + send button, auto-scroll to newest, online indicator.
 * Uses Identity API: GET/POST /identity/groups/:id/messages
 */

import type { AweniContext } from '../../types';

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  text: string;
  timestamp: string;
  isOwn: boolean;
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w.charAt(0)).join('').toUpperCase().slice(0, 2);
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return time;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

function getMockMessages(currentUserId: string): ChatMessage[] {
  return [
    { id: 'm1', senderId: 'u1', senderName: 'Ahmed K.', text: 'Assalamu alaikum everyone! Who wants to join a math challenge this week?', timestamp: new Date(Date.now() - 7200000).toISOString(), isOwn: false },
    { id: 'm2', senderId: 'u2', senderName: 'Sara M.', text: 'Wa alaikum assalam! Count me in! I have been practicing multiplication games.', timestamp: new Date(Date.now() - 6000000).toISOString(), isOwn: false },
    { id: 'm3', senderId: currentUserId, senderName: 'You', text: 'Sounds great! Let us set a target of 20 games this week.', timestamp: new Date(Date.now() - 5400000).toISOString(), isOwn: true },
    { id: 'm4', senderId: 'u3', senderName: 'Youssef B.', text: 'I just finished my Quran review for today. 12 cards done!', timestamp: new Date(Date.now() - 3600000).toISOString(), isOwn: false },
    { id: 'm5', senderId: 'u2', senderName: 'Sara M.', text: 'MashaAllah Youssef! Keep up the great work.', timestamp: new Date(Date.now() - 3000000).toISOString(), isOwn: false },
    { id: 'm6', senderId: 'u1', senderName: 'Ahmed K.', text: 'I created the challenge. Check the Challenges tab!', timestamp: new Date(Date.now() - 1800000).toISOString(), isOwn: false },
  ];
}

export class GroupChat {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  private groupId: string = '';
  private messages: ChatMessage[] = [];
  private pollInterval: ReturnType<typeof setInterval> | null = null;

  async mount(container: HTMLElement, ctx: AweniContext, groupId: string): Promise<void> {
    this.container = container;
    this.ctx = ctx;
    this.groupId = groupId;

    // Fetch messages
    try {
      const resp = await ctx.api.get<ChatMessage[]>(`/aweni/api/v1/identity/groups/${groupId}/messages`);
      this.messages = resp;
    } catch {
      const userId = ctx.auth.user?.id || 'u7';
      this.messages = getMockMessages(userId);
    }

    this.renderChat();

    // Poll for new messages every 30s (lightweight)
    this.pollInterval = setInterval(() => this.fetchNewMessages(), 30000);
  }

  destroy(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.container = null;
    this.ctx = null;
  }

  private async fetchNewMessages(): Promise<void> {
    if (!this.ctx) return;
    try {
      const resp = await this.ctx.api.get<ChatMessage[]>(`/aweni/api/v1/identity/groups/${this.groupId}/messages`);
      if (resp.length > this.messages.length) {
        this.messages = resp;
        this.renderChat();
      }
    } catch {
      // Silently ignore polling errors
    }
  }

  private renderChat(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const onlineCount = Math.max(1, Math.floor(Math.random() * 4) + 1);

    this.container.innerHTML = `
      <!-- Online indicator -->
      <div class="px-4 py-2 flex items-center gap-2 border-b" style="border-color: var(--border)">
        <span class="w-2 h-2 rounded-full bg-green-500"></span>
        <span class="text-xs" style="color: var(--text-secondary)">${t('community_members_online', { count: onlineCount })}</span>
      </div>

      <!-- Messages -->
      <div class="chat-messages-scroll px-4 py-3 space-y-3 overflow-y-auto" style="max-height: 320px; scroll-behavior: smooth">
        ${this.messages.length === 0 ? `
          <p class="text-center text-xs py-6" style="color: var(--text-secondary)">${t('community_no_messages')}</p>
        ` : this.messages.map((msg) => this.renderMessage(msg)).join('')}
      </div>

      <!-- Input -->
      <div class="px-4 py-3 border-t flex items-center gap-2" style="border-color: var(--border)">
        <input type="text" class="chat-input flex-1 px-4 py-2.5 rounded-full text-sm" placeholder="${t('community_type_message')}" style="background: var(--bg-secondary); color: var(--text-primary); border: none; outline: none" />
        <button class="chat-send-btn w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-transform active:scale-90" style="background: #6366F1; color: white" aria-label="${t('chat_send')}">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    `;

    // Auto-scroll to bottom
    const scrollArea = this.container.querySelector('.chat-messages-scroll');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }

    // Bind send
    const input = this.container.querySelector('.chat-input') as HTMLInputElement;
    const sendBtn = this.container.querySelector('.chat-send-btn');

    const sendMessage = async () => {
      const text = input?.value?.trim();
      if (!text || !this.ctx) return;
      input.value = '';

      const newMsg: ChatMessage = {
        id: `m${Date.now()}`,
        senderId: this.ctx.auth.user?.id || 'me',
        senderName: this.ctx.auth.user?.name || 'You',
        text,
        timestamp: new Date().toISOString(),
        isOwn: true,
      };

      this.messages.push(newMsg);
      this.renderChat();

      // Send to API (fire-and-forget)
      try {
        await this.ctx.api.post(`/aweni/api/v1/identity/groups/${this.groupId}/messages`, { text });
      } catch {
        // Message already shown optimistically
      }
    };

    sendBtn?.addEventListener('click', sendMessage);
    input?.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') sendMessage();
    });
  }

  private renderMessage(msg: ChatMessage): string {
    if (msg.isOwn) {
      return `
        <div class="flex justify-end">
          <div class="max-w-[80%]">
            <div class="rounded-2xl rounded-br-md px-4 py-2.5" style="background: #6366F1; color: white">
              <p class="text-sm">${this.escapeHtml(msg.text)}</p>
            </div>
            <p class="text-[10px] mt-0.5 text-end" style="color: var(--text-secondary)">${formatTime(msg.timestamp)}</p>
          </div>
        </div>
      `;
    }

    return `
      <div class="flex gap-2">
        <div class="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-1" style="background: #6366F120; color: #6366F1">
          ${getInitials(msg.senderName)}
        </div>
        <div class="max-w-[80%]">
          <p class="text-[10px] font-semibold mb-0.5" style="color: var(--text-secondary)">${msg.senderName}</p>
          <div class="rounded-2xl rounded-bl-md px-4 py-2.5" style="background: var(--bg-secondary)">
            <p class="text-sm" style="color: var(--text-primary)">${this.escapeHtml(msg.text)}</p>
          </div>
          <p class="text-[10px] mt-0.5" style="color: var(--text-secondary)">${formatTime(msg.timestamp)}</p>
        </div>
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    const el = document.createElement('span');
    el.textContent = text;
    return el.innerHTML;
  }
}
